// Domain enums — the CANONICAL source of truth, mirrored (not generated) by
// the Prisma `enum` declarations in `apps/api/prisma/schema.prisma`
// (owned by database-engineer, spec §3.3). Any change here must be applied
// there too, and vice versa — keep the value lists byte-identical.
//
// Modelled as TS `const` object + derived union type (not `enum`) so the
// package stays framework-agnostic, tree-shakeable, and produces plain
// string literals at the API boundary (RFC 7807 bodies, zod DTOs, JSON) with
// no runtime enum-object baggage.
//
// §15 sign-off deltas applied (locked ahead of Phase 1, see
// docs/eskayscience-system-spec.md §15.10 "Admin vs Teacher"):
//   - THREE roles: ADMIN, TEACHER, STUDENT (spec §2/§3.3 text still says
//     "two user types" — superseded by the sign-off; ADMIN owns board/level
//     management, roster, and billing, TEACHER is authoring-only).
//   - UserStatus gains PENDING_VERIFICATION for the self-registration flow
//     (§15.8 open registration + paywall path, resolved to: open self-signup
//     allowed, gated behind email verification before ACTIVE).

// ─── Identity ────────────────────────────────────────────────────────────

export const Role = {
  ADMIN: 'ADMIN',
  TEACHER: 'TEACHER',
  STUDENT: 'STUDENT',
} as const;
export type Role = (typeof Role)[keyof typeof Role];
export const ROLE_VALUES = Object.values(Role) as Role[];

/**
 * Strict role-hierarchy ordinal (feat/teacher-password-reset, security
 * review-specified) — `ADMIN=2 > TEACHER=1 > STUDENT=0`. This is the RANK
 * PRIMITIVE, not a description of the current password-reset rule — see
 * `canAdminSuperuserResetPassword` below for that (2026-09-12 PO ruling:
 * ADMIN is a password-reset super-user and may reset ANY account, including
 * another ADMIN and itself, bypassing this ordinal comparison entirely).
 * `ROLE_RANK[actor.role] > ROLE_RANK[target.role]` (STRICTLY greater, never
 * a pairwise allowlist table) governs the TEACHER leg of password-reset —
 * TEACHER may reset STUDENT only, never TEACHER, never ADMIN — via
 * `outranksForPasswordReset` below, which `canAdminSuperuserResetPassword`'s
 * ADMIN short-circuit falls through to for non-ADMIN actors. The
 * account-lock/unlock check (`PATCH /v1/users/:id/status`) does NOT use this
 * rank primitive — see {@link canLockAccount} below, which implements the
 * 2026-09-13 PO ruling (ADMIN may lock any account including another ADMIN;
 * TEACHER may lock STUDENT only) as its own standalone predicate. */
export const ROLE_RANK: Record<Role, number> = {
  [Role.STUDENT]: 0,
  [Role.TEACHER]: 1,
  [Role.ADMIN]: 2,
};

/** True iff `actorRole` strictly outranks `targetRole` per {@link ROLE_RANK}.
 * Never same-rank, never lower-rank. This is the general strict-rank
 * primitive shared by more than one privileged-action rank check — keep it
 * a single ordinal comparison, not a pairwise allowlist, and do NOT widen it
 * for one call site's sake (see `canAdminSuperuserResetPassword` below,
 * which is intentionally a SEPARATE predicate rather than a mutation of this
 * one). Current call site: the TEACHER-resets-STUDENT / ADMIN-resets-below
 * leg of `POST /v1/users/:id/password-reset`
 * (`apps/api/src/routes/users.ts`). The account-lock/unlock check
 * (`PATCH /v1/users/:id/status`) does NOT reuse this predicate — see
 * {@link canLockAccount}. */
export function outranksForPasswordReset(actorRole: Role, targetRole: Role): boolean {
  return ROLE_RANK[actorRole] > ROLE_RANK[targetRole];
}

/**
 * PO ruling 2026-09-12 (`docs/ARCHITECTURE_DECISIONS.md`, "Admin
 * password-reset super-user"): ADMIN is a password-reset super-user and may
 * reset ANY account's password, including another ADMIN and including
 * itself. This predicate exists ONLY for
 * `POST /v1/users/:id/password-reset` — it deliberately does NOT touch
 * {@link outranksForPasswordReset} / {@link ROLE_RANK}, and is NOT reused by
 * {@link canLockAccount}: its semantics (ADMIN may act on self) are the
 * opposite of what account-locking wants (ADMIN self-lock is always
 * refused). TEACHER's authority is unchanged: still strictly-outranks
 * (STUDENT only, never TEACHER/ADMIN).
 */
export function canAdminSuperuserResetPassword(actorRole: Role, targetRole: Role): boolean {
  if (actorRole === Role.ADMIN) return true;
  return outranksForPasswordReset(actorRole, targetRole);
}

/**
 * PO ruling 2026-09-13 (`docs/ARCHITECTURE_DECISIONS.md`, "Account
 * lock/unlock authorization matrix"): lock and suspend are the same concept,
 * widening `PATCH /v1/users/:id/status`. ADMIN may lock ANY account,
 * including another ADMIN (supersedes the 2026-09-11 "ADMIN cannot suspend
 * ADMIN" rule). TEACHER may lock STUDENT accounts only — never another
 * TEACHER, never an ADMIN. Role-only: this project has no teacher-student
 * ownership/roster-scoping model, so no such scoping is applied here.
 *
 * This predicate is standalone and deliberately does NOT reuse
 * {@link outranksForPasswordReset} or {@link canAdminSuperuserResetPassword}.
 * It contains NO identity/self check by design — self-lock is refused by an
 * explicit guard in the route handler (`apps/api/src/routes/users.ts`) that
 * MUST run before this predicate is evaluated; under this matrix an ADMIN
 * actor passes this check against an ADMIN target, so that handler-level
 * self-check is the only thing preventing admin self-lockout.
 */
export function canLockAccount(actorRole: Role, targetRole: Role): boolean {
  if (actorRole === Role.ADMIN) return true;
  if (actorRole === Role.TEACHER) return targetRole === Role.STUDENT;
  return false;
}

export const UserStatus = {
  ACTIVE: 'ACTIVE',
  INVITED: 'INVITED',
  SUSPENDED: 'SUSPENDED',
  PENDING_VERIFICATION: 'PENDING_VERIFICATION',
} as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];
export const USER_STATUS_VALUES = Object.values(UserStatus) as UserStatus[];

// Sign in with Google — the external identity provider a linked
// `AuthIdentity` row came from. Byte-identical to the
// `AuthIdentity_provider_check` CHECK constraint in
// apps/api/migrations/0020_auth_identity.sql. Single-member today
// (GOOGLE-only per design), modelled as an object (not a bare string
// literal) so a future provider only ever needs an addition here, never a
// call-site rewrite.
export const AuthProvider = {
  GOOGLE: 'GOOGLE',
} as const;
export type AuthProvider = (typeof AuthProvider)[keyof typeof AuthProvider];
export const AUTH_PROVIDER_VALUES = Object.values(AuthProvider) as AuthProvider[];

export const VerificationPurpose = {
  EMAIL_VERIFY: 'EMAIL_VERIFY',
  PASSWORD_RESET: 'PASSWORD_RESET',
  INVITE: 'INVITE',
} as const;
export type VerificationPurpose = (typeof VerificationPurpose)[keyof typeof VerificationPurpose];
export const VERIFICATION_PURPOSE_VALUES = Object.values(
  VerificationPurpose,
) as VerificationPurpose[];

// ─── Curriculum tree (§3.1, §3.3) ──────────────────────────────────────────

export const SectionKind = {
  CHAPTER: 'CHAPTER',
  UNIT: 'UNIT',
  TOPIC: 'TOPIC',
  MODULE: 'MODULE',
} as const;
export type SectionKind = (typeof SectionKind)[keyof typeof SectionKind];
export const SECTION_KIND_VALUES = Object.values(SectionKind) as SectionKind[];

export const TabKind = {
  LECTURE_NOTES: 'LECTURE_NOTES',
  LECTURE_VIDEOS: 'LECTURE_VIDEOS',
  SIMULATIONS: 'SIMULATIONS',
  PRINTABLES: 'PRINTABLES',
  CUSTOM: 'CUSTOM',
} as const;
export type TabKind = (typeof TabKind)[keyof typeof TabKind];
export const TAB_KIND_VALUES = Object.values(TabKind) as TabKind[];

export const ResourceType = {
  NOTE: 'NOTE',
  VIDEO: 'VIDEO',
  SIMULATION: 'SIMULATION',
  PDF: 'PDF',
  LINK: 'LINK',
} as const;
export type ResourceType = (typeof ResourceType)[keyof typeof ResourceType];
export const RESOURCE_TYPE_VALUES = Object.values(ResourceType) as ResourceType[];

export const AssetStatus = {
  PENDING: 'PENDING',
  READY: 'READY',
  FAILED: 'FAILED',
  QUARANTINED: 'QUARANTINED',
} as const;
export type AssetStatus = (typeof AssetStatus)[keyof typeof AssetStatus];
export const ASSET_STATUS_VALUES = Object.values(AssetStatus) as AssetStatus[];

// P2-03 (R2 asset service, spec §7.1) — the upload-time classification a
// caller declares on `POST /v1/assets/upload-url` (`{ ..., kind }`), used to
// pick the MIME allowlist + size cap enforced on finalise (§7.1: "PDF:
// application/pdf; VIDEO: video/mp4, video/quicktime; IMAGE: png/jpeg/webp/
// avif", "size caps (PDF 50 MB, video 2 GB, image 10 MB)"). Deliberately NOT
// mirrored by a Prisma column/enum — `Asset` (apps/api/prisma/schema.prisma)
// has no `kind` field; the three allowlists are disjoint by MIME top-level
// type (`application/pdf` vs `video/*` vs `image/*`), so `apps/api/src/
// services/r2.ts`'s `mimeTypeToAssetKind` derives the same value straight
// back from the already-persisted `Asset.mimeType` column at finalise time
// with no separate storage needed — see that file's header for the full
// rationale.
export const AssetKind = {
  PDF: 'PDF',
  VIDEO: 'VIDEO',
  IMAGE: 'IMAGE',
} as const;
export type AssetKind = (typeof AssetKind)[keyof typeof AssetKind];
export const ASSET_KIND_VALUES = Object.values(AssetKind) as AssetKind[];

// ─── Access & commerce (§3.2, §3.3) ────────────────────────────────────────

export const EnrollmentStatus = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED',
  PENDING_PAYMENT: 'PENDING_PAYMENT',
} as const;
export type EnrollmentStatus = (typeof EnrollmentStatus)[keyof typeof EnrollmentStatus];
export const ENROLLMENT_STATUS_VALUES = Object.values(EnrollmentStatus) as EnrollmentStatus[];

// P4-01: how an Enrollment row came to exist — a teacher/admin hand-grant
// (MANUAL), the checkout -> webhook -> entitlement pipeline (CHECKOUT), or a
// bulk roster import (IMPORT). Provider-agnostic (no Stripe/SSLCommerz
// specifics) — mirrored by the `Enrollment_source_check` CHECK constraint in
// apps/api/migrations/0002_commerce_schema_check.sql.
export const EnrollmentSource = {
  MANUAL: 'MANUAL',
  CHECKOUT: 'CHECKOUT',
  IMPORT: 'IMPORT',
} as const;
export type EnrollmentSource = (typeof EnrollmentSource)[keyof typeof EnrollmentSource];
export const ENROLLMENT_SOURCE_VALUES = Object.values(EnrollmentSource) as EnrollmentSource[];

export const ScopeType = {
  GLOBAL: 'GLOBAL',
  LEVEL: 'LEVEL',
  SUBJECT: 'SUBJECT',
  SECTION: 'SECTION',
} as const;
export type ScopeType = (typeof ScopeType)[keyof typeof ScopeType];
export const SCOPE_TYPE_VALUES = Object.values(ScopeType) as ScopeType[];

export const GrantMode = {
  ALLOW: 'ALLOW',
  DENY: 'DENY',
} as const;
export type GrantMode = (typeof GrantMode)[keyof typeof GrantMode];
export const GRANT_MODE_VALUES = Object.values(GrantMode) as GrantMode[];

// ADR-packages-entitlements §1.2 — the three-tier discriminated grant
// target (`Package.grantType`). Mirrors the migration's three-branch CHECK
// (exactly one of examBoardId/levelId/subjectId set, matching this value).
export const PackageGrantType = {
  BOARD: 'BOARD',
  LEVEL: 'LEVEL',
  SUBJECT: 'SUBJECT',
} as const;
export type PackageGrantType = (typeof PackageGrantType)[keyof typeof PackageGrantType];
export const PACKAGE_GRANT_TYPE_VALUES = Object.values(PackageGrantType) as PackageGrantType[];

// ADR §1.5 — `Package.status`. NEVER part of the entitlement predicate
// (services/entitledScopes.ts, Slice B) — withdrawing a package from sale
// (ACTIVE -> INACTIVE) stops new sales only, existing `StudentPackage` rows
// keep access until their own window expires.
export const PackageStatus = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;
export type PackageStatus = (typeof PackageStatus)[keyof typeof PackageStatus];
export const PACKAGE_STATUS_VALUES = Object.values(PackageStatus) as PackageStatus[];

// ADR §1.6 — `StudentPackage.status`. Same value set as `EnrollmentStatus`
// by design (mirrors that model field-for-field) but kept as its own named
// enum rather than reused, matching this file's per-entity convention (see
// `EnrollmentSource` vs a hypothetical shared "Source" type).
export const StudentPackageStatus = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  REVOKED: 'REVOKED',
  PENDING_PAYMENT: 'PENDING_PAYMENT',
} as const;
export type StudentPackageStatus = (typeof StudentPackageStatus)[keyof typeof StudentPackageStatus];
export const STUDENT_PACKAGE_STATUS_VALUES = Object.values(
  StudentPackageStatus,
) as StudentPackageStatus[];

// ADR §1.6/§6.4 — how a `StudentPackage` row came to exist. `MANUAL` is the
// only producer Slice C builds (`services/studentPackages.ts`'s
// `grantPackageToStudent` seam); `CHECKOUT`/`IMPORT`/`MIGRATION` are reserved
// for later slices, same "one writer, several sources" shape as
// `EnrollmentSource`.
export const StudentPackageSource = {
  MANUAL: 'MANUAL',
  CHECKOUT: 'CHECKOUT',
  IMPORT: 'IMPORT',
  MIGRATION: 'MIGRATION',
} as const;
export type StudentPackageSource = (typeof StudentPackageSource)[keyof typeof StudentPackageSource];
export const STUDENT_PACKAGE_SOURCE_VALUES = Object.values(
  StudentPackageSource,
) as StudentPackageSource[];

// ─── Ops / abuse analysis (§10, §3.3) ──────────────────────────────────────

// P2-22 (tasks/current/phase-2/P2-22-login-attempt-logging.md): every call
// into `POST /v1/auth/login` writes one `LoginAttempt` row for later abuse
// analysis, now that the old per-IP+email hard lockout is gone (a single
// NAT'd-IP school no longer trips a false lockout). This is a SEPARATE,
// finer-grained outcome set than `ErrorCode` (errors.ts) deliberately: the
// login route's actual HTTP response collapses "no such account" and "wrong
// password" into the SAME `AUTH_INVALID_CREDENTIALS` 401 (spec §10
// constant-time / non-enumerating response — see routes/auth.ts's `/login`
// handler), but the internal analysis log needs the real distinction to be
// useful for abuse detection (e.g. credential stuffing against known emails
// vs. scanning for valid ones) — this enum is NEVER surfaced in an HTTP
// response body, only persisted to `LoginAttempt.outcome` (mirrored by the
// `LoginAttempt_outcome_check` CHECK constraint in
// apps/api/migrations/0005_login_attempt.sql).
export const LoginAttemptOutcome = {
  SUCCESS: 'SUCCESS',
  UNKNOWN_USER: 'UNKNOWN_USER',
  BAD_CREDENTIALS: 'BAD_CREDENTIALS',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
} as const;
export type LoginAttemptOutcome = (typeof LoginAttemptOutcome)[keyof typeof LoginAttemptOutcome];
export const LOGIN_ATTEMPT_OUTCOME_VALUES = Object.values(
  LoginAttemptOutcome,
) as LoginAttemptOutcome[];

// P2-25 (docs/design/P2-25-command-channel-contract.md §1.2/§2) — the poll-based
// backend -> frontend control bus. Value list must stay byte-identical to the
// `UserCommand_type_check` CHECK constraint in
// apps/api/migrations/0007_command_queue.sql.
export const CommandType = {
  FORCE_LOGOUT: 'FORCE_LOGOUT',
  JOB_STATUS: 'JOB_STATUS',
  RELOAD: 'RELOAD',
  DATA_CHANGED: 'DATA_CHANGED',
} as const;
export type CommandType = (typeof CommandType)[keyof typeof CommandType];
export const COMMAND_TYPE_VALUES = Object.values(CommandType) as CommandType[];

// Topbar notification feed (T3, docs/design/topbar-technical-approach.md §7).
// v1 event-type set — producers land in later tasks, this only needs to
// exist and stay byte-identical to the `Notification_type_check` CHECK
// constraint in apps/api/migrations/0021_notification_model.sql.
// TOPBAR-T16 (2026-09): added the three account/security values below.
// NOT YET LIVE — the corresponding CHECK-constraint migration
// (apps/api/migrations/0030_notification_account_security_types.sql) is
// written but its table-rebuild DDL is escalated to the Product Owner and
// UNAPPLIED (local or remote) as of this commit. Do not wire producers for
// these three values until that migration has actually been applied,
// or every insert will violate the still-six-value CHECK and fail at
// runtime.
export const NotificationType = {
  ENROLLMENT_GRANTED: 'ENROLLMENT_GRANTED',
  ENROLLMENT_EXPIRING: 'ENROLLMENT_EXPIRING',
  IMPORT_FINISHED: 'IMPORT_FINISHED',
  PAYMENT_SUCCEEDED: 'PAYMENT_SUCCEEDED',
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  SECTION_PUBLISHED: 'SECTION_PUBLISHED',
  EMAIL_VERIFIED: 'EMAIL_VERIFIED',
  PASSWORD_CHANGED: 'PASSWORD_CHANGED',
  INVITE_ACCEPTED: 'INVITE_ACCEPTED',
} as const;
export type NotificationType = (typeof NotificationType)[keyof typeof NotificationType];
export const NOTIFICATION_TYPE_VALUES = Object.values(NotificationType) as NotificationType[];
